import type { WorkoutCycle } from '@/api/workout/types/workout-cycle';
import type { WorkoutEvent } from '@/api/workout/types/workout';
import { EFFECTIVE_FULL_COMPLETION_THRESHOLD } from '@/utils/is-effectively-completed-workout';
import { estimateRewardCompletionPercentage, shouldUseEarlyExitFlow } from './should-use-early-exit-flow';

export type RecoveryFinishAction = 'early_exit' | 'complete' | 'insufficient_state';

const toFinite = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

/** Mirrors backend getExerciseOnlyDurationFromCycles (seconds). */
export function getExerciseOnlyDurationSeconds(cycles: WorkoutCycle[] | undefined): number {
  if (!cycles?.length) return 0;
  return cycles.reduce((acc, cycle) => {
    const exerciseSec = (cycle.sets || []).reduce((sum, set) => sum + toFinite(set.amount) * toFinite(set.time), 0);
    return acc + exerciseSec * Math.max(1, toFinite(cycle.repeats, 1));
  }, 0);
}

/** Mirrors backend getTotalMinutesFromCycles (seconds, despite the name). */
export function getPlannedSessionSeconds(cycles: WorkoutCycle[] | undefined): number {
  if (!cycles?.length) return 0;
  return cycles.reduce((acc, cycle) => {
    const sets = cycle.sets || [];
    const exerciseSec = sets.reduce((sum, set) => sum + toFinite(set.amount) * toFinite(set.time), 0);
    const setCount = sets.reduce((sum, set) => sum + toFinite(set.amount), 0);
    const restSec = Math.max(0, setCount - 1) * toFinite(cycle.restTime);
    const cycleSec = (exerciseSec + restSec + toFinite(cycle.breakTime)) * Math.max(1, toFinite(cycle.repeats, 1));
    return acc + cycleSec;
  }, 0);
}

export function hasExerciseActiveDuration(value: number | null | undefined): boolean {
  return value !== null && value !== undefined && Number.isFinite(value);
}

export function canEstimateRewardFromCheckpoint(event: Pick<WorkoutEvent, 'cycles' | 'completePercentage'>): boolean {
  const progress = event.completePercentage;
  if (!Number.isFinite(progress)) return false;
  return getExerciseOnlyDurationSeconds(event.cycles) > 0 || getPlannedSessionSeconds(event.cycles) > 0;
}

/**
 * Same gate as finish-from-checkpoint: min(progress, activeTime).
 * progress < 80 is always early-exit (min cannot reach 80).
 * progress >= 80 needs planned duration + active/session time.
 */
export function resolveRecoveryFinishDecision(
  event: Pick<WorkoutEvent, 'cycles' | 'completePercentage' | 'duration' | 'exerciseActiveDuration'>
): { action: RecoveryFinishAction; rewardCompletionPercentage: number | null } {
  const progress = event.completePercentage;
  if (!Number.isFinite(progress)) {
    return { action: 'insufficient_state', rewardCompletionPercentage: null };
  }

  if (progress < EFFECTIVE_FULL_COMPLETION_THRESHOLD) {
    return { action: 'early_exit', rewardCompletionPercentage: progress };
  }

  const plannedExerciseOnlyMs = getExerciseOnlyDurationSeconds(event.cycles) * 1000;
  const plannedSessionMs = getPlannedSessionSeconds(event.cycles) * 1000;
  const useExerciseActive = hasExerciseActiveDuration(event.exerciseActiveDuration);

  if (useExerciseActive && plannedExerciseOnlyMs > 0) {
    const reward = estimateRewardCompletionPercentage({
      progressPercentage: progress,
      exerciseActiveDurationMs: event.exerciseActiveDuration as number,
      plannedExerciseOnlyMs,
    });
    return {
      action: shouldUseEarlyExitFlow(reward) ? 'early_exit' : 'complete',
      rewardCompletionPercentage: reward,
    };
  }

  if (!useExerciseActive && plannedSessionMs > 0 && Number.isFinite(event.duration)) {
    const reward = estimateRewardCompletionPercentage({
      progressPercentage: progress,
      exerciseActiveDurationMs: event.duration,
      plannedExerciseOnlyMs: plannedSessionMs,
    });
    return {
      action: shouldUseEarlyExitFlow(reward) ? 'early_exit' : 'complete',
      rewardCompletionPercentage: reward,
    };
  }

  return { action: 'insufficient_state', rewardCompletionPercentage: null };
}
